import React, { useEffect, useRef, useState } from "react";

import shrimpIconSrc from "../../../static/img/shrimp-icon-2x.png";

import styles from "./ShrimpAnimation.module.scss";

const SHRIMP_PER_ROW = 12;
const ROW_HEIGHT = 40;
const MAX_VIEWPORT = 800;
const VISIBLE_ROWS = Math.ceil(MAX_VIEWPORT / ROW_HEIGHT);
const BUFFER_ROWS = VISIBLE_ROWS + 1;
const SLIDE_SAFETY_TIMEOUT = 500;

interface Props {
    seconds: number;
}

const ShrimpIcon = () => (
    <img
        src={shrimpIconSrc}
        width="28"
        height="28"
        alt=""
        className={styles.shrimpIcon}
        aria-hidden="true"
    />
);

const Dot = () => (
    <svg className={styles.dot} viewBox="0 0 10 10" aria-hidden="true">
        <circle cx="5" cy="5" r="5" fill="rgb(223 168 80)" />
    </svg>
);

/** Position within the current consumption cycle (1-based, 1 to SHRIMP_PER_ROW). */
const getCyclePosition = (seconds: number): number => {
    const totalElapsed = seconds === 0 ? 60 : 60 - seconds;
    return ((totalElapsed - 1) % SHRIMP_PER_ROW) + 1;
};

export const ShrimpAnimation = ({ seconds }: Props) => {
    const cyclePos = getCyclePosition(seconds);
    const fullyConsumed = cyclePos - 1;

    const [isSliding, setIsSliding] = useState(false);
    const prevSecondsRef = useRef(seconds);

    // Detect slide trigger synchronously during render (not in an effect)
    // to avoid a single painted frame where the cycle resets but isSliding is false.
    // eslint-disable-next-line react-hooks/refs -- intentional "adjust state during render" pattern per React docs
    const prevCyclePos = getCyclePosition(prevSecondsRef.current);
    if (prevCyclePos === SHRIMP_PER_ROW && cyclePos === 1 && !isSliding) {
        setIsSliding(true);
    }
    // eslint-disable-next-line react-hooks/refs
    prevSecondsRef.current = seconds;

    // Safety fallback: reset if onTransitionEnd never fires
    // (e.g. tab backgrounded, element unmounted mid-transition).
    useEffect(() => {
        if (!isSliding) return;
        const timeout = setTimeout(
            () => setIsSliding(false),
            SLIDE_SAFETY_TIMEOUT,
        );
        return () => clearTimeout(timeout);
    }, [isSliding]);

    const handleTransitionEnd = () => {
        setIsSliding(false);
    };

    const renderShrimpRow = () =>
        Array.from({ length: SHRIMP_PER_ROW }, (_, i) => (
            <div className={styles.cell} key={i}>
                <ShrimpIcon />
            </div>
        ));

    const renderRow = (rowIndex: number) => {
        // During slide: row 0 is the completed dots row sliding away,
        // all other rows are fresh shrimp.
        if (isSliding) {
            if (rowIndex === 0) {
                return (
                    <div className={styles.row} key={rowIndex}>
                        {Array.from({ length: SHRIMP_PER_ROW }, (_, i) => (
                            <div className={styles.cell} key={i}>
                                <Dot />
                            </div>
                        ))}
                    </div>
                );
            }
            return (
                <div className={styles.row} key={rowIndex}>
                    {renderShrimpRow()}
                </div>
            );
        }

        // Not sliding: only row 0 is active (consuming shrimp).
        // When cyclePos === 1 and we just finished sliding, show fresh shrimp.
        if (rowIndex !== 0 || (cyclePos === 1 && !isSliding)) {
            return (
                <div className={styles.row} key={rowIndex}>
                    {renderShrimpRow()}
                </div>
            );
        }

        return (
            <div className={styles.row} key={rowIndex}>
                {Array.from({ length: SHRIMP_PER_ROW }, (_, i) => {
                    const isConsumed = i < fullyConsumed;

                    return (
                        <div className={styles.cell} key={i}>
                            {isConsumed ? <Dot /> : <ShrimpIcon />}
                        </div>
                    );
                })}
            </div>
        );
    };

    return (
        <div className={styles.root}>
            <div className={styles.viewport}>
                <div
                    className={`${styles.track} ${isSliding ? styles.sliding : ""}`}
                    onTransitionEnd={handleTransitionEnd}
                >
                    {Array.from({ length: BUFFER_ROWS }, (_, i) =>
                        renderRow(i),
                    )}
                </div>
            </div>
        </div>
    );
};
